import type { CameraAlarmActivation } from "@visioni/a3s-services-core";
import { apiRequest, getResolvedApiBaseUrl } from "./request";
import { AlarmConfiguration } from "@visioni/a3s-alarm-schedules/schema";

export type LivestreamSession = {
  start: string;
  action: "POST";
  timeout: string; // ISO 8601 duration, e.g., "PT30S"
};

export type LivestreamFormat = "http-mp4" | "mjpeg" | "http-mjpeg" | "mjpg";

export type Livestream = {
  format: LivestreamFormat;
  session: LivestreamSession;
};

export type LivestreamStartResponse = {
  streamUrl: string;
};

export type CameraApiResponse = {
  site: { tenant: string; id: string };
  key: { asset: string; id: string };
  isOnline: boolean;
  alarmStatus: CameraAlarmActivation | null;
  snapshot: { url: string; capturedAt: string } | null;
  livestreams?: Livestream[];
  nickname?: string;
  name?: string;
  model?: string;
  alarmSettings?: AlarmConfiguration;
  snapshotRefreshInterval?: number;
  allowedActions?: string[];
  hasGate?: boolean;
  /** What the camera's asset is hired for; absent from pre-DEL-743 API versions. */
  features?: string[];
};

export const camerasApi = {
  async list(tenantId: string, siteId: string): Promise<CameraApiResponse[]> {
    const cacheBuster = Date.now();
    return apiRequest<CameraApiResponse[]>({
      method: "GET",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras`,
      params: { _t: cacheBuster },
      logMessage: "Fetching cameras from:",
    });
  },

  async search(tenantId: string): Promise<CameraApiResponse[]> {
    const cacheBuster = Date.now();
    return apiRequest<CameraApiResponse[]>({
      method: "GET",
      path: `/api/v1/tenants/${tenantId}/cameras`,
      params: {
        _t: cacheBuster,
      },
      logMessage: "Searching cameras at:",
    });
  },

  async get(tenantId: string, siteId: string, cameraKey: string): Promise<CameraApiResponse> {
    // Add cache-busting parameter to force fresh URLs
    const cacheBuster = Date.now();
    return apiRequest<CameraApiResponse>({
      method: "GET",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}?_t=${cacheBuster}`,
      logMessage: "Fetching camera from:",
    });
  },

  async updateSilence(
    tenantId: string,
    siteId: string,
    cameraKey: string,
    silence: { id: string; reason?: string; period: { start: unknown; end: unknown } } | null,
    timezone: string,
  ): Promise<AlarmConfiguration> {
    return apiRequest<AlarmConfiguration>({
      method: "POST",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/silence`,
      body: { silence, timezone },
      logMessage: "Updating alarm silence at:",
    });
  },

  /**
   * Start a livestream session.
   * The pre-signed Lambda URL streams video/mp4 directly (chunked transfer).
   * No fetch needed - just return the URL for the video player to use.
   */
  async startLivestreamSession(sessionStartPath: string): Promise<LivestreamStartResponse> {
    let url: string;

    if (sessionStartPath.startsWith("http")) {
      url = sessionStartPath;
    } else {
      // Relative path, add base URL
      url = `${await getResolvedApiBaseUrl()}${sessionStartPath}`;
    }

    console.log("[Livestream] Stream URL:", url);

    // The Lambda URL IS the stream - it returns video/mp4 directly
    return { streamUrl: url };
  },

  async updateNickname(
    tenantId: string,
    siteId: string,
    cameraKey: string,
    nickname: string | null,
  ): Promise<{ nickname: string | null }> {
    return apiRequest<{ nickname: string | null }>({
      method: "PUT",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}`,
      body: { nickname },
      logMessage: "Updating camera nickname at:",
    });
  },

  async getJpegStreamUrl(tenantId: string, siteId: string, cameraKey: string): Promise<string | null> {
    const response = await apiRequest<{ image: { url: string } | null }>({
      method: "GET",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/stream`,
      logMessage: "Fetching JPEG stream URL:",
    });
    return response.image?.url ?? null;
  },
};
